import { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { judicialService } from "@/services/api";

function daysBetween(d1: string, d2: string): number {
  return Math.ceil((new Date(d2).getTime() - new Date(d1).getTime()) / (1000 * 60 * 60 * 24));
}

interface JudicialRequest {
  id: string;
  caso_id?: string;
  estudiante_id: string;
  tribunal: string;
  rit_ruc: string;
  fecha_recepcion: string;
  fecha_vencimiento: string;
  tipo_requerimiento?: string;
  estado: string;
  oficio_url?: string;
  estudiante_nombre: string;
  estudiante_curso: string;
  caso_folio?: string;
}

export default function JudicializadosPage() {
  const navigate = useNavigate();
  const todayStr = new Date().toISOString().split("T")[0];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requerimientos, setRequerimientos] = useState<JudicialRequest[]>([]);

  useEffect(() => {
    async function loadData() {
      try {
        setError("");
        const data = await judicialService.list();
        if (Array.isArray(data)) {
          setRequerimientos(data);
        }
      } catch (err: any) {
        console.error("Error loading judicial requests:", err);
        setError("Error al cargar requerimientos judiciales: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const activeRequerimientos = useMemo(() => {
    return requerimientos.filter((r) => r.estado !== "respondido");
  }, [requerimientos]);

  if (loading) {
    return (
      <div className="p-12 text-center flex flex-col items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-2" />
        <p className="text-sm text-foreground-500">Cargando requerimientos judiciales...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Casos Judicializados</h1>
        <p className="text-sm text-foreground-500 mt-0.5">Estudiantes con requerimientos de tribunal activos</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      {activeRequerimientos.length === 0 ? (
        <div className="text-center py-16 text-foreground-400 bg-white border border-background-200/80 rounded-2xl">
          <i className="ri-scales-3-line text-4xl mb-3 block" />
          <p className="text-sm font-medium">Sin casos judicializados activos</p>
          <p className="text-xs mt-1">Los casos con requerimientos de tribunal aparecerán aquí</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeRequerimientos.map((req) => {
            const d = req.fecha_vencimiento ? daysBetween(todayStr, req.fecha_vencimiento) : null;
            const overdue = d !== null && d < 0;
            return (
              <div
                key={req.id}
                onClick={() => req.caso_id && navigate(`/casos/${req.caso_id}`)}
                className="bg-white border border-background-200/80 rounded-2xl p-5 hover:border-primary-300 transition-all cursor-pointer"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                      <span className="text-xs font-mono text-foreground-400">{req.caso_folio || "SIN CASO ASOCIADO"}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        overdue ? "bg-red-100 text-red-700" : d !== null && d <= 5 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"
                      }`}>
                        {overdue ? "VENCIDO" : d !== null && d <= 5 ? `${d}d restantes` : "Al día"}
                      </span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950">{req.estudiante_nombre} · {req.estudiante_curso}</h3>
                    <p className="text-sm text-foreground-600 mt-1">
                      Requerimiento: {req.tipo_requerimiento ? req.tipo_requerimiento.replace(/_/g, " ") : "Informe Judicial"}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-foreground-500">
                      <span><strong>Tribunal:</strong> {req.tribunal}</span>
                      <span><strong>RIT/RUC:</strong> {req.rit_ruc}</span>
                      <span className={overdue ? "text-red-600 font-bold" : ""}>
                        <strong>Vencimiento:</strong> {req.fecha_vencimiento}
                      </span>
                    </div>
                  </div>
                  {req.caso_id && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/casos/${req.caso_id}`); }}
                        className="px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Ver expediente
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}